import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Upload, Images, Globe, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MissingImageDialog({ product, onClose, onDone }) {
  const [tab, setTab] = useState("upload");
  const [media, setMedia] = useState(null);
  const [mq, setMq] = useState("");
  const [busy, setBusy] = useState(false);

  const loadMedia = useCallback(() => {
    api.get("/admin/seo/media", { params: { q: mq, page_size: 40 } })
      .then((r) => setMedia(r.data.items)).catch(() => setMedia([]));
  }, [mq]);
  useEffect(() => { if (tab === "library") loadMedia(); }, [tab, loadMedia]);

  const apply = async (url) => {
    setBusy(true);
    try {
      await api.post(`/admin/seo/products/${product.id}/set-image`, { url });
      toast.success("Image set"); onDone && onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set image"); }
    finally { setBusy(false); }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f); fd.append("kind", "image");
    try {
      const r = await fetch(`${BACKEND}/api/admin/upload`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${localStorage.getItem("acp_token")}` },
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      await apply(`${BACKEND}${data.url}`);
    } catch { toast.error("Upload failed"); setBusy(false); }
  };

  const pullSupplier = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/seo/products/${product.id}/pull-supplier-image`);
      toast.success("Pulled supplier image"); onDone && onDone(); onClose();
      return r;
    } catch (e) { toast.error(e?.response?.data?.detail || "No supplier image found"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="missing-image-dialog">
        <DialogHeader>
          <DialogTitle>Manage image — {product.name}</DialogTitle>
          <DialogDescription>Upload a new photo, pick one from your media library, or pull the authorised supplier image.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 border-b mb-4">
          {[["upload", "Upload", Upload], ["library", "Media library", Images], ["supplier", "Supplier feed", Globe]].map(([id, label, Icon]) => (
            <button key={id} data-testid={`mi-tab-${id}`} onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === id ? "border-accent text-steel" : "border-transparent text-muted-foreground"}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        {busy && <div className="py-6 grid place-items-center text-muted-foreground"><Loader2 className="animate-spin" /></div>}
        {!busy && tab === "upload" && (
          <div className="py-6 text-center">
            <label className="inline-flex flex-col items-center gap-2 cursor-pointer rounded-xl border-2 border-dashed p-8 hover:bg-slate-50">
              <Upload className="text-accent" /> <span className="text-sm font-semibold text-steel">Choose an image to upload</span>
              <span className="text-xs text-muted-foreground">PNG / JPG / WEBP, max 25MB</span>
              <input type="file" accept="image/*" className="hidden" onChange={uploadFile} data-testid="mi-upload-input" />
            </label>
          </div>
        )}
        {!busy && tab === "library" && (
          <div className="space-y-3">
            <Input placeholder="Search media…" value={mq} onChange={(e) => setMq(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadMedia()} data-testid="mi-media-search" />
            {!media ? <p className="text-sm text-muted-foreground">Loading…</p>
              : media.length === 0 ? <p className="text-sm text-muted-foreground">No media found.</p>
              : <div className="grid grid-cols-4 gap-2 max-h-80 overflow-y-auto">
                  {media.map((m, i) => (
                    <button key={i} onClick={() => apply(`${BACKEND}${m.url}`)} data-testid={`mi-media-${i}`}
                      className="aspect-square rounded-lg border overflow-hidden hover:ring-2 ring-accent">
                      <img src={`${BACKEND}${m.url}`} alt={m.name} className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>}
          </div>
        )}
        {!busy && tab === "supplier" && (
          <div className="py-6 text-center space-y-3">
            {product.product_url
              ? <>
                  <p className="text-sm text-muted-foreground">Pull the product photo from the authorised supplier page:</p>
                  <a href={product.product_url} target="_blank" rel="noreferrer" className="text-xs text-steel underline break-all">{product.product_url}</a>
                  <div><Button onClick={pullSupplier} data-testid="mi-pull-supplier"><Globe size={15} className="mr-1.5" /> Pull supplier image</Button></div>
                </>
              : <p className="text-sm text-muted-foreground">No authorised supplier URL stored on this product.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
